/**
 * 官网咨询线索。
 *
 * 数据来自 `hew3-website-source` 的「预约咨询」表单（POST /api/public/consultations）。
 * 这里只做两件事：看线索、标记跟进状态；**不自动把线索变成成员**。
 *
 * 口径：
 *   - 列表与汇总全部来自 `/api/admin/consultations`，界面不自己累加；
 *   - 403 时如实显示服务端原文，不伪装成空列表；
 *   - 状态只能 new → contacted → closed，跟进备注可选。
 */
import { useState } from 'react';
import { adminApi, ApiError } from '../lib/api.ts';
import type { LeadItem } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, Kpi, PageHead } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import { formatDateTime, formatText } from '../lib/format.ts';

interface Toast {
  text: string;
  tone: 'ok' | 'error';
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'ghost'> = {
  new: 'warn',
  contacted: 'ok',
  closed: 'ghost',
};

const STATUS_LABEL: Record<string, string> = {
  new: '待跟进',
  contacted: '已联系',
  closed: '已关闭',
};

export function LeadsPage() {
  const [filter, setFilter] = useState<'all' | 'new' | 'contacted' | 'closed'>('all');
  const [toast, setToast] = useState<Toast | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad(
    (signal) => adminApi.leads(filter === 'all' ? '' : filter, 100, signal),
    [filter],
  );

  if (loading) return <LoadingBox text="正在加载官网咨询线索…" />;
  if (error) return <ErrorBox message={errorText(error)} onRetry={reload} />;
  if (!data) return <ErrorBox message="咨询线索列表为空" onRetry={reload} />;

  const handleDone = (result: Toast): void => {
    setToast(result);
    window.setTimeout(() => setToast(null), 5000);
  };

  const update = async (item: LeadItem, status: 'contacted' | 'closed'): Promise<void> => {
    const note = window.prompt(`将「${item.name}」标记为${STATUS_LABEL[status]}，可填写跟进备注（可留空）：`, item.note);
    if (note === null) return;
    setBusyId(item.id);
    try {
      const result = await adminApi.updateLead(item.id, status, note);
      handleDone({ text: `已更新「${result.item.name}」为${STATUS_LABEL[result.item.status] ?? result.item.status}`, tone: 'ok' });
      reload();
    } catch (err) {
      handleDone({
        text: err instanceof ApiError ? `更新被拒绝（${err.status}）：${err.message}` : '更新请求失败，请确认后端服务可用',
        tone: 'error',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHead
        title="官网咨询线索"
        sub="来自官网「提交预约咨询」表单。线索不会自动注册为成员；由运营人员联系后标记状态。"
        actions={
          <select
            className="field-input"
            style={{ width: 180 }}
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
          >
            <option value="all">全部状态</option>
            <option value="new">待跟进</option>
            <option value="contacted">已联系</option>
            <option value="closed">已关闭</option>
          </select>
        }
      />

      <Card title="状态汇总" sub="计数来自 /api/admin/consultations 的 summary">
        <div className="grid4">
          <Kpi label="全部线索" value={data.summary.total} delta="官网表单累计" />
          <Kpi label="待跟进" value={data.summary.new} tone="warn" delta="尚未联系" />
          <Kpi label="已联系" value={data.summary.contacted} tone="e" delta="已建立联系" />
          <Kpi label="已关闭" value={data.summary.closed} delta="无需继续跟进" />
        </div>
      </Card>

      <Card tight>
        {data.items.length === 0 ? (
          <EmptyBox
            title="当前没有咨询线索"
            desc="官网表单提交后会出现在这里；如果刚上线，请先在官网提交一条测试数据。"
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>提交时间</th>
                  <th>姓名 / 公司</th>
                  <th>电话</th>
                  <th>咨询方向</th>
                  <th>咨询内容</th>
                  <th>状态</th>
                  <th>跟进备注</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id}>
                    <td className="mono-num">{formatDateTime(item.createdAt)}</td>
                    <td>
                      <b>{item.name}</b>
                      <div className="muted text-tiny">{formatText(item.company)}</div>
                    </td>
                    <td className="mono-num">{item.phone}</td>
                    <td>{formatText(item.interest)}</td>
                    <td style={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>{formatText(item.message)}</td>
                    <td>
                      <Badge tone={STATUS_TONE[item.status] ?? 'ghost'}>
                        {STATUS_LABEL[item.status] ?? item.status}
                      </Badge>
                    </td>
                    <td>{formatText(item.note)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {item.status === 'new' && (
                        <button type="button" className="link" disabled={busyId === item.id} onClick={() => void update(item, 'contacted')}>
                          标记已联系
                        </button>
                      )}
                      {item.status !== 'closed' && (
                        <>
                          {' '}
                          <button type="button" className="link" disabled={busyId === item.id} onClick={() => void update(item, 'closed')}>
                            关闭
                          </button>
                        </>
                      )}
                      {item.status === 'closed' && <span className="muted text-tiny">已处理</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="note-src">
        <b>数据边界</b>
        <br />
        线索只包含访客主动填写的姓名、公司、电话、咨询方向与内容，不会自动创建成员账号；
        需要开户时请走「成员与会籍 → 建档」，由运营确认后创建。
      </div>

      {toast && <div className={`toast ${toast.tone}`}>{toast.text}</div>}
      <div className="sp8" />
    </div>
  );
}
